import {Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges, Output, EventEmitter} from '@angular/core';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { NgClass } from '@angular/common';
import { Subscription, } from 'rxjs';
import { ApplicationDialogService } from '../application-dialog-component/application-dialog.service';
import { PortalHotToastService } from '../portal-hot-toast-component/hot-toast.service';
import { Application, ApplicationWebService, CreditorWatchReportData, DEFAULT_LIMIT, DEFAULT_OFFSET, DownloadCreditorWatchReportFn, GetApplicationPartiesPayload, GetCreditWebServicesFn, NewWebService, WEB_SERVICE_STALE_AFTER_DAYS, getApplicationStatus, isWebServiceStale } from '@portal-workspace/grow-shared-library';
import { creditWebServiceApplicant } from './credit-result-parties';
import { FlexModule } from '@angular/flex-layout';
import { CustomContentLoaderComponent } from "../custom-content-loader-component/custom-content-loader.component";
import { CustomPaginatorComponent } from '../custom-paginator-component/custom-paginator/custom-paginator.component';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { loadingFor } from '@ngneat/loadoff';
import { DatePipe, AsyncPipe, } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import moment from 'moment';
import { bureauReportDownloadTarget, openAuthorizedBureauReport } from './open-bureau-report-download';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';

interface WebServiceDataSource extends NewWebService {
  applicant: string;
  applicantType: string;
  isMostRecent: boolean;
  isStale: boolean;
}

const CLOSED_APPLICATION_STATUSES = ['Settled', 'Declined', 'Withdrawn', 'Closed'];

@Component({
    selector: 'credit-result-web-services',
    templateUrl: './credit-result-web-services.component.html',
    styleUrls: ['./credit-result-web-services.component.scss'],
    standalone: true,
    imports: [
      MatTableModule, 
      NgClass, 
      ExtendedModule, 
      FlexModule,
      CustomContentLoaderComponent,
      CustomPaginatorComponent,
      MatSortModule,
      DatePipe,
      AsyncPipe,
      MatButtonModule,
      MatInputModule,
      MatFormFieldModule,
      ReactiveFormsModule,
      MatTooltipModule,
      MatIconModule,
    ]
})
export class CreditResultWebServicesComponent implements OnInit, OnChanges, OnDestroy {
  @Input({required: true}) data!: NewWebService[];
  @Input({required: true}) application!: Application;
  @Input() parties: GetApplicationPartiesPayload | null = null;
  @Input({required: true}) downloadCreditorWatchReportFn!: DownloadCreditorWatchReportFn;
  @Input({required: true}) getCreditWebServicesFn!: GetCreditWebServicesFn;
  @Input() apiUrl!: string;
  @Output() viewReport: EventEmitter<NewWebService> = new EventEmitter();

  subscriptions: Subscription[] = [];
  moment = moment;
  formControlSearch!: FormControl<string | null>;

  total = 0;
  limit = DEFAULT_LIMIT;
  offset = DEFAULT_OFFSET;
  filter = '';
  loader = loadingFor('tableLoading');
  sorts: { prop: string, dir: 'asc' | 'desc' } | null = null;
  dataSource: WebServiceDataSource[] = [];
  columnsToDisplay = ['applicant', 'type', 'lastUpdatedTime', 'report'];
  readonly staleTooltip = `This file is more than ${WEB_SERVICE_STALE_AFTER_DAYS} days old, you may want to rerun it`;
  showSeeMore = true;
  isExpanded = false;
  isLoadingMore = false;

  constructor(
    private formBuilder: FormBuilder,
    private toastService: PortalHotToastService,
    private dialogService: ApplicationDialogService,
  ) {
  }

  ngOnInit(): void {
    this.loadDataSource();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.loadDataSource();
  }

  loadDataSource() {
    this.dataSource = [];

    // Identify the most recent record per person/entity + type
    // (mirrors backend PARTITION BY individualFirstName, individualLastName, individualDoB, ABN, ACN, type)
    const mostRecentByPersonAndType = this.getMostRecentByPersonAndType(this.data);
    const applicationIsOpen = !this.isApplicationClosed();

    for (const webService of this.data) {
      const { applicant, applicantType } = creditWebServiceApplicant(this.parties, webService);

      const serviceKey = this.getServiceKey(webService);
      const groupKey = this.getPersonAndTypeKey(webService);
      const mostRecentService = mostRecentByPersonAndType.get(groupKey);
      const isMostRecent = mostRecentService !== undefined && 
                           this.getServiceKey(mostRecentService) === serviceKey;

      const dataSourceItem: WebServiceDataSource = {
        ...webService,
        applicant,
        applicantType,
        isMostRecent,
        isStale: applicationIsOpen && isWebServiceStale(webService.lastUpdatedTime),
      };

      this.dataSource.push(dataSourceItem);
    }
  }

  private isApplicationClosed(): boolean {
    const status = getApplicationStatus(this.application);
    return !!status && CLOSED_APPLICATION_STATUSES.includes(status);
  }

  private getServiceKey(webService: NewWebService): string {
    // Create a unique key for the service
    // Use id if available, otherwise use a combination of fields
    if (webService.id !== undefined) {
      return `id:${webService.id}`;
    }
    return `${webService.type}:${webService.level}:${webService.lastUpdatedTime}:${webService.ABN || ''}:${webService.ACN || ''}:${webService.individualFirstName || ''}:${webService.individualLastName || ''}`;
  }

  // Group key for "most recent" bucketing — must match backend SQL
  // PARTITION BY individualFirstName, individualLastName, individualDoB, ABN, ACN, type
  private getPersonAndTypeKey(webService: NewWebService): string {
    const firstName = (webService.individualFirstName ?? '').toUpperCase();
    const lastName = (webService.individualLastName ?? '').toUpperCase();
    const dob = webService.individualDoB ?? '';
    const abn = webService.ABN ?? '';
    const acn = webService.ACN ?? '';
    return `${webService.type}|${firstName}|${lastName}|${dob}|${abn}|${acn}`;
  }

  private getMostRecentByPersonAndType(webServices: NewWebService[]): Map<string, NewWebService> {
    const mostRecentByPersonAndType = new Map<string, NewWebService>();

    for (const webService of webServices) {
      const key = this.getPersonAndTypeKey(webService);
      const existing = mostRecentByPersonAndType.get(key);

      if (!existing) {
        mostRecentByPersonAndType.set(key, webService);
      } else {
        const existingTime = moment(existing.lastUpdatedTime);
        const currentTime = moment(webService.lastUpdatedTime);

        if (currentTime.isAfter(existingTime)) {
          mostRecentByPersonAndType.set(key, webService);
        }
      }
    }

    return mostRecentByPersonAndType;
  }

  getColumnTitles(column: string): string {
    switch (column) {
      case 'applicant': return 'Applicant';
      case 'type': return 'Type';
      case 'report': return 'Report';
      case 'lastUpdatedTime': return 'Updated Time';
      default: return column;
    }
  }

  onDownloadReport(element: NewWebService) {
    const pdfRequestId = element.type === 'CreditorWatch'
      ? (element.json as unknown as CreditorWatchReportData).pdfRequestId
      : undefined;
    const target = bureauReportDownloadTarget(element.type, element.id, pdfRequestId);
    if (!target) {
      return;
    }
    openAuthorizedBureauReport(this.apiUrl, target.kind, target.resourceId).catch(() => {
      this.toastService.error('Unable to download report');
    });
  }

  onViewReport(element: NewWebService) {
    this.viewReport.emit(element)
  }

  onViewCreditorWatchReport(element: NewWebService) {
    this.dialogService.openCreditorWatchReportDialog({
      webService: element
    }).afterClosed().subscribe()
  }

  getWebServiceUpdatedTime(webService: NewWebService) {
    return moment(webService.lastUpdatedTime).format("DD/MM/YYYY h:mm a");
  }

  onViewJson(element: ApplicationWebService) {
    this.dialogService.openWebServiceJsonDialog({
      webService: element
    }).afterClosed().subscribe()
  }

  onSeeMore() {
    const shouldShowAll = !this.isExpanded;
    this.isLoadingMore = true;
    
    this.subscriptions.push(
      this.getCreditWebServicesFn(this.application.ApplicationId, shouldShowAll).subscribe((webServices: NewWebService[]) => {
        this.data = webServices;
        this.loadDataSource();
        this.isExpanded = shouldShowAll;
        this.isLoadingMore = false;
        
        const message = shouldShowAll 
          ? 'Loaded all web services successfully' 
          : 'Showing filtered web services';
        this.toastService.success(message);
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
